import React, { FC } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { FileType } from 'src/Libs/installation-statement/types'
import PaperContainer from '../PaperContainer'
import useInstallationFiles from 'src/Libs/documents/hooks/use-installation-files'
import { FileWithLatestStatus } from 'src/Libs/installation-statement/models'
import { useTableColumnSize } from '../../hooks/use-table-column-size'
import { Box, Button, CircularProgress, Tooltip } from '@mui/material'
import useModals from 'src/Hooks/useModals'
import { useReleaseInstallationStatement } from '../../hooks/use-release-installation-statement'
import { InstallationStatementModel } from 'src/Libs/installation-statement/models/statement'
import { formatInTimeZone, BERLIN_TIME_ZONE } from '@e3dc-react/shell/Libs/date'
import { FileLink } from 'src/Libs/installation-details/components'
import { UseQueryResult } from '@tanstack/react-query'
import { WarrantyInfo } from 'src/Libs/installation-details/api'

interface Props {
  currentInstallationStatement?: InstallationStatementModel
  installationId?: string
  refetchCurrentStorageInstallationStatement: () => Promise<UseQueryResult>
  files: FileWithLatestStatus[]
  filesLoading?: boolean
  refetchFiles: () => Promise<UseQueryResult>
  warrantyInfo: WarrantyInfo | null
}

/**
 * Component to display documents for selected system.
 */
const Documents: FC<Props> = ({
  installationId,
  currentInstallationStatement,
  refetchCurrentStorageInstallationStatement,
  files,
  filesLoading,
  refetchFiles,
  warrantyInfo,
}) => {
  const { t } = useCustomTranslation()
  const columnWidth = useTableColumnSize()
  const { openConfirmationModal, openUpdateWarrantyDialog } = useModals()
  const { releaseInstallationStatement } = useReleaseInstallationStatement()
  const deviceLinkId = currentInstallationStatement?.deviceLinkId ?? 0
  const hasValidStatement = !!currentInstallationStatement?.installationRequirements
  const hasValidWarranty = warrantyInfo?.isValid ?? hasValidStatement
  const installationIdToUse = installationId ? Number.parseInt(installationId) : undefined

  const getTypeValue = (type: FileType): string => {
    if (type === 'warranty') {
      return t('system-details.installation-statements.warranty')
    }
    if (type === 'summary') {
      return t('system-details.installation-statements.title')
    }

    return ''
  }

  const onConfirmRelease = (onClose: () => void): void => {
    releaseInstallationStatement(installationId, deviceLinkId.toString()).then(() => refetchCurrentStorageInstallationStatement())
    onClose()
  }

  const openConfirmationDialog = (): void => {
    openConfirmationModal({
      onConfirm: onClose => onConfirmRelease(onClose),
      buttonText: [t('common.cancel'), t('system-details.installation-statements.release.button.accept')],
      text: t('system-details.installation-statements.release.text'),
      title: t('system-details.installation-statements.release.title'),
    })
  }

  const columns: Header<FileWithLatestStatus>[] = [
    {
      value: 'createdAt',
      label: t('common.date'),
      width: columnWidth,
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: (value: string) => {
        return formatInTimeZone(new Date(value), BERLIN_TIME_ZONE, t('common.format.date-fns-date-time-without-seconds'))
      },
    },
    {
      value: 'isLatestVersion',
      label: t('system-details.installation-statements.image-table.status'),
      width: columnWidth,
      isSortable: true,
      sortFunction: nullsLast((a: boolean, b: boolean) => `${a}`.localeCompare(`${b}`)),
      valueRender: (value: boolean, _, item) => {
        if (value && hasValidStatement && item?.type === 'summary') {
          return t('system-details.installation-statements.image-table.statuses.current')
        }

        if (value && hasValidWarranty && item?.type === 'warranty') {
          return t('system-details.installation-statements.image-table.statuses.current')
        }

        return t('system-details.installation-statements.image-table.statuses.deprecated')
      },
    },
    {
      value: 'name',
      label: t('system-details.installation-statements.image-table.file-name'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: (text, _, data) => (
        <FileLinkItem
          installationId={installationIdToUse}
          data={data}
          text={text}
          hasValidStatement={hasValidStatement}
          hasValidWarranty={hasValidWarranty}
        />
      ),
    },
    {
      value: 'type',
      label: t('common.type'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => getTypeValue(value),
    },
    {
      value: 'isLatestVersion',
      valueRender: (value, _, item) => {
        if (value && item?.type === 'summary') {
          return (
            <Tooltip title={!hasValidStatement ? t('system-details.installation-statements.release.button.tooltip') : undefined}>
              <Box>
                <Button variant="outlined" color="primary" onClick={openConfirmationDialog} disabled={!hasValidStatement}>
                  {t('system-details.installation-statements.release.button.label')}
                </Button>
              </Box>
            </Tooltip>
          )
        } else if (value && hasValidWarranty && item?.type === 'warranty') {
          return (
            <Button
              variant={'outlined'}
              color={'primary'}
              onClick={() =>
                openUpdateWarrantyDialog({
                  installationId,
                  deviceLinkId,
                  installationStatementId: currentInstallationStatement?.id,
                  refetchFiles: refetchFiles,
                })
              }
            >
              {t('system-details.installation-statements.update-warranty.button.label-service')}
            </Button>
          )
        }
      },
    },
  ]

  return (
    <PaperContainer title={t('documents.title')}>
      {filesLoading ? (
        <Box display="flex" alignItems="center" justifyContent="center">
          <CircularProgress sx={{ margin: 'auto' }} />
        </Box>
      ) : (
        <TableComplete
          noDataText={t('system-details.installation-statements.image-table.no-elements')}
          header={columns}
          data={files ?? []}
          initialSort={{
            name: 'createdAt',
            direction: 'desc',
          }}
          headerElevation={0}
          bodyElevation={0}
          footerElevation={0}
          coloredColumn
          disablePagination
          allowOtherCellColors
        />
      )}
    </PaperContainer>
  )
}
interface FileLinkProps {
  data?: FileWithLatestStatus
  hasValidStatement: boolean
  hasValidWarranty: boolean
  installationId?: number
  text: string
}

const FileLinkItem: React.FC<FileLinkProps> = ({ data, hasValidWarranty, hasValidStatement, installationId, text }) => {
  const { openFileInNewTab } = useInstallationFiles()
  const isValid = (data?.type === 'warranty' && hasValidWarranty) || (data?.type === 'summary' && hasValidStatement)
  const isDisabled = !(data?.isLatestVersion && isValid)

  const handleClick = (): void => {
    if (!data || !installationId) return

    openFileInNewTab(installationId, data.id, data.mimeType)
  }

  return <FileLink data={data} disabled={isDisabled} onClick={handleClick} text={text} />
}

export default Documents
